import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import UserProfile from "./User";


/**
 * Gate a page on being signed in, and on what kind of account is signed in.
 *
 *   <AccessController>                     signed in (the default)
 *   <AccessController onlyAdmin={false}>   signed in, any role
 *   <AccessController require="owner">     owners only
 *
 * This is convenience, not security. It stops someone wandering into a page
 * that will only show them errors; the endpoints behind each page enforce the
 * same rule themselves, because a client can be told anything.
 */
function AccessController(props) {
    // Default parameters rather than defaultProps, which React 18 deprecates
    // for function components and warns about on every render.
    const {
        restricted = true,
        onlyAdmin = true,
        require = null,
        children,
    } = props;
    const navigate = useNavigate();

    const checkSession = useCallback(async () => {
        try {
            const isValid = await UserProfile.verifySession();
            if (!isValid) {
                UserProfile.logout();
                navigate("/");
            }
        } catch (error) {
            console.error('Session check error:', error);
            UserProfile.logout();
            navigate("/");
        }
    }, [navigate]);

    useEffect(() => {
        if (restricted) {
            if (!UserProfile.isLoggedIn()) {
                navigate("/");
                return;
            }
            // Initial session check
            checkSession();

            // Set up periodic session checks (every 5 minutes)
            const sessionCheckInterval = setInterval(checkSession, 5 * 60 * 1000);

            return () => {
                clearInterval(sessionCheckInterval);
            };
        }
    }, [restricted, checkSession, navigate]);

    useEffect(() => {
        if (!UserProfile.isLoggedIn()) return;

        // Send someone who lacks the role to the home screen rather than to the
        // login page: they are signed in perfectly well, just not for this.
        if (require === 'owner' && !UserProfile.isOwner()) {
            navigate("/home");
            return;
        }
        if (onlyAdmin && !UserProfile.isManager()) {
            navigate("/home");
        }
    }, [onlyAdmin, require, navigate]);


    return (
        <>
            {children}
        </>
    )
}


export default AccessController;
